import { useObserver } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import {
  featureCurrencies,
  useCurrencyCode,
} from '@aboutyou/legacy-shared/src/utils/currency'
import { useFilterContext } from 'sharedComponents/ProductFilter/FilterProvider'
import { useFeatureFlags } from 'store/context'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'

type RangeType = [number, number]

const usePriceRange = (
  {
    min,
    max,
  }: {
    min: number
    max: number
  },
  {
    min: availableMin,
    max: availableMax,
  }: {
    min: number
    max: number
  },
): [RangeType, (range: RangeType) => void] => {
  const [localPriceRange, setLocalPriceRange] = useState<RangeType>([min, max])
  const updateInBounds = ([nextMin, nextMax]: RangeType) =>
    setLocalPriceRange([
      Math.max(nextMin, availableMin),
      Math.min(nextMax, availableMax),
    ])

  useEffect(() => {
    updateInBounds([min, max])
  }, [min, max, availableMin, availableMax])

  return [localPriceRange, updateInBounds]
}

export const usePriceFilter = () => {
  const filterStore = useFilterStore()
  let currencyCode = useCurrencyCode()
  const { productCurrencyCode } = useFilterContext()
  const { isActive: isFeatureActive } = useFeatureFlags()

  const enableSecondaryLabel = isFeatureActive('enableSecondaryLabel')

  const { minPrice, maxPrice } = useObserver(() => filterStore.selectedFilters)
  const availablePriceFilter = useObserver(
    () => filterStore.availablePriceFilter,
  )

  const availableMinPrice = availablePriceFilter
    ? availablePriceFilter.values[0].min
    : 0
  const availableMaxPrice = availablePriceFilter
    ? availablePriceFilter.values[0].max
    : 0
  const selectedMinPrice = minPrice?.length ? parseInt(minPrice[0], 10) : 0
  const selectedMaxPrice = maxPrice?.length
    ? parseInt(maxPrice[0], 10)
    : Infinity

  let min = selectedMinPrice || availableMinPrice
  const max = selectedMaxPrice || availableMaxPrice
  if (min > availableMaxPrice) {
    min = 0
  }

  const [localPriceRange, setLocalPriceRange] = usePriceRange(
    {
      min,
      max,
    },
    {
      min: availableMinPrice,
      max: availableMaxPrice,
    },
  )
  if (enableSecondaryLabel) {
    currencyCode =
      productCurrencyCode === featureCurrencies.BGN
        ? featureCurrencies.BGN
        : featureCurrencies.EUR
  }

  return {
    range: localPriceRange,
    updateIntermediateRange: setLocalPriceRange,
    updateRange: filterStore.updateSelectedPriceRange,
    availableMinPrice,
    availableMaxPrice,
    currencyCode,
    reset: () => {
      filterStore.clearFilterGroup('minPrice')
      filterStore.clearFilterGroup('maxPrice')
    },
  }
}
